import type { IconDefinition } from '@fortawesome/pro-regular-svg-icons'
import type { Toast } from './interfaces'
import type { ToastVariant } from './types'
import type { CSSProperties } from 'vue'

import {
  faCircleCheck,
  faCircleExclamation,
  faCircleInfo,
  faTriangleExclamation,
} from '@fortawesome/pro-solid-svg-icons'

interface ToastItemPayload {
  toast: Toast
}
interface ToastItemReturn {
  icons: Record<ToastVariant, IconDefinition>
  progress: Ref<number>
  swipeStyle: ComputedRef<CSSProperties>
  onPointerDown: (e: PointerEvent) => void
  onPointerMove: (e: PointerEvent) => void
  onPointerUp: (options?: { onEmitDismiss?: () => void }) => void
  pauseProgress: () => void
  resumeProgress: () => void
}

export const useToastItem = (payload: ToastItemPayload): ToastItemReturn => {
  const SWIPE_DISMISS_THRESHOLD = 100
  const SWIPE_OPACITY_DIVISOR = 200

  const icons: Record<ToastVariant, IconDefinition> = {
    success: faCircleCheck,
    warning: faTriangleExclamation,
    error: faCircleExclamation,
    info: faCircleInfo,
  }

  // --- Swipe to dismiss ---
  const startX = ref(0)
  const currentX = ref(0)
  const isSwiping = ref(false)

  const onPointerDown = (e: PointerEvent): void => {
    startX.value = e.clientX
    currentX.value = 0
    isSwiping.value = true
    ;(e.currentTarget as HTMLElement).setPointerCapture(e.pointerId)
  }

  const onPointerMove = (e: PointerEvent): void => {
    if (!isSwiping.value) return
    currentX.value = e.clientX - startX.value
  }

  const onPointerUp = (options?: { onEmitDismiss?: () => void }): void => {
    if (!isSwiping.value) return
    isSwiping.value = false

    if (Math.abs(currentX.value) > SWIPE_DISMISS_THRESHOLD) {
      options?.onEmitDismiss?.()
    } else {
      currentX.value = 0
    }
  }

  const swipeStyle = computed<CSSProperties>(() => {
    if (currentX.value === 0) return {}
    return {
      transform: `translateX(${currentX.value}px)`,
      opacity: Math.max(0, 1 - Math.abs(currentX.value) / SWIPE_OPACITY_DIVISOR),
      transition: isSwiping.value ? 'none' : 'transform 0.2s ease, opacity 0.2s ease',
    }
  })

  // --- Progress bar ---
  const progress = ref(SWIPE_DISMISS_THRESHOLD)
  let animationFrame: number | null = null
  let startTime: number | null = null
  const isPaused = ref(false)
  let remaining = payload.toast.duration || 4000

  const startProgress = (): void => {
    startTime = performance.now()
    const animate = (now: number) => {
      if (isPaused.value) {
        animationFrame = requestAnimationFrame(animate)
        return
      }
      if (!startTime) startTime = now
      const elapsed = now - startTime
      const fraction = Math.max(0, 1 - elapsed / remaining)
      progress.value = fraction * SWIPE_DISMISS_THRESHOLD

      if (fraction > 0) {
        animationFrame = requestAnimationFrame(animate)
      }
    }
    animationFrame = requestAnimationFrame(animate)
  }

  const pauseProgress = (): void => {
    isPaused.value = true
    if (startTime) {
      remaining = remaining - (performance.now() - startTime)
    }
  }

  const resumeProgress = (): void => {
    isPaused.value = false
    startTime = performance.now()
  }

  onMounted(() => {
    if (payload.toast.duration && payload.toast.duration > 0) {
      startProgress()
    }
  })

  onBeforeUnmount(() => {
    if (animationFrame) {
      cancelAnimationFrame(animationFrame)
    }
  })

  return {
    icons,
    progress,
    swipeStyle,
    onPointerDown,
    onPointerMove,
    onPointerUp,
    pauseProgress,
    resumeProgress,
  }
}
